import { NextPage } from "next";
import { useIntl } from "react-intl";
import { PageProps } from "@/pages/_app";
import Layout from "@/components/layout/Layout";
import { SEO } from "@/interfaces/seo";
import { SEOWithoutTemplate } from "@/interfaces/seoWithoutTemplate";
import HeroBlock, {
  HeroBlockProps,
} from "@/components/common/heroBlock/HeroBlock";
import { VehicleType } from "@/components/common/searchBlock/children/vehicleTypeSelection/VehicleTypeSelection";
import { Article, ArticleProps } from "@/components/common/article/Article";
import {
  DescriptiveBlockList,
  DescriptiveBlockListProps,
} from "@/components/common/descriptiveBlockList/DescriptiveBlockList";
import { Section } from "@/components/common/section/Section";
import { CategoryBlock } from "@/components/common/categoryBlock/CategoryBlock";
import { CategoryCardProps } from "@/components/common/categoryBlock/categoryCard/CategoryCard";
import { StaticMap } from "@/components/common/staticMap/StaticMap";
import { FAQProps } from "@/utils/formatCategoryPage";
import { FAQ } from "@/components/pages/vehicle/categoryPage/faq/FAQ";

import { Feature, isFeatureEnabled } from "@/utils/isFeatureEnabled";
import ReviewsBlock2 from "@/components/common/reviewsBlockV1/ReviewsBlock";
import ReviewsBlock1, {
  RentACarReview,
} from "@/components/common/reviewsBlock/ReviewsBlock";
import { StructuredReviewProps } from "@/components/common/structuredData/StructuredData.interface";
import styles from "./LicenseFree.module.scss";

const ReviewsBlock = ({
  name,
  agency,
  rentACarReview,
  reviewsConst,
  image,
}: StructuredReviewProps) => {
  if (!isFeatureEnabled(Feature.NEW_REVIEWS_BLOCK)) {
    return (
      <ReviewsBlock1
        name={name}
        agency={agency}
        rentACarReview={rentACarReview}
        reviewsConst={reviewsConst}
        image={image}
      />
    );
  } else {
    return (
      <ReviewsBlock2
        name={name}
        agency={agency}
        rentACarReview={rentACarReview}
        reviewsConst={reviewsConst}
        image={image}
      />
    );
  }
};

export type LicenseFreeContent = {
  heroBlock: HeroBlockProps;
  vehicleType: VehicleType;
  presentation: ArticleProps | undefined;
  descriptiveBlockList: DescriptiveBlockListProps | undefined;
  categoryPresentation: ArticleProps | undefined;
  categoryCards: CategoryCardProps[] | undefined;
  faq?: FAQProps;
  rentACarReview: RentACarReview | undefined;
  seo: SEO;
  seoWithoutTemplate: SEOWithoutTemplate;
};
type LicenseFreeProps = PageProps &
  LicenseFreeContent & {
    defaultCanonicalRoute: string;
  };

export const LicenseFree: NextPage<LicenseFreeProps> = ({
  layout,
  reviewsConst,
  heroBlock,
  vehicleType,
  presentation,
  descriptiveBlockList,
  categoryPresentation,
  categoryCards,
  faq,
  rentACarReview,
  seo,
  seoWithoutTemplate,
  defaultCanonicalRoute,
}: LicenseFreeProps) => {
  const intl = useIntl();

  return (
    <>
      <Layout
        layout={layout}
        defaultCanonicalRoute={defaultCanonicalRoute}
        pageMeta={seo}
        title={seoWithoutTemplate.title}
        description={seoWithoutTemplate.description}
        breadcrumbs={[
          { title: intl.formatMessage({ id: "home" }), url: "/" },
          {
            title: intl.formatMessage({
              id: `licenseFree${vehicleType}Breadcrumb`,
            }),
            url: defaultCanonicalRoute,
          },
        ]}
      >
        <HeroBlock
          {...heroBlock}
          searchBlockInitialState={{
            vehicleType: VehicleType.CAR_WITHOUT_LICENCE,
            isOneWayTrip: false,
          }}
        />
        <Section>
          {presentation && <Article {...presentation} />}
          {descriptiveBlockList && (
            <DescriptiveBlockList {...descriptiveBlockList} />
          )}
          {categoryPresentation && <Article {...categoryPresentation} />}
        </Section>
        <div className={styles.desktopContainer}>
          {categoryCards && <CategoryBlock categoryCards={categoryCards} />}
        </div>
        <Section>
          <StaticMap mapName={"agences-sans-permis-rent-a-car_828748"} />
        </Section>

        {faq && <FAQ {...faq} />}

        {rentACarReview && (
          <ReviewsBlock
            rentACarReview={rentACarReview}
            reviewsConst={reviewsConst}
          />
        )}
      </Layout>
    </>
  );
};
